/**
 * IOSShortcutCard - Gestion du raccourci iOS pour ajouter des cibles
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Smartphone, Copy, RefreshCw, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { API, toast } from '../../App';

export const IOSShortcutCard = () => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);
  const [copiedPrompt, setCopiedPrompt] = useState(false);

  // URL de production
  const PROD_URL = 'https://auto-edito.com';

  useEffect(() => {
    loadToken();
  }, []);

  const loadToken = async () => {
    try {
      const res = await axios.get(`${API}/targets/my-shortcut-token`);
      setToken(res.data.token);
    } catch (e) {
      console.error('Erreur chargement token:', e);
    } finally {
      setLoading(false);
    }
  };

  const generateToken = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/targets/generate-shortcut-token`);
      setToken(res.data.token);
      toast.success('Token généré !');
    } catch (e) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedToken(true);
      toast.success('Token copié !');
      setTimeout(() => setCopiedToken(false), 2000);
    } catch (e) {
      toast.error('Erreur de copie');
    }
  };

  const copyPrompt = async () => {
    const prompt = `Crée un raccourci iOS pour partager des liens vers Auto Edito. Voici la logique :

1. Récupère l'entrée du raccourci (Shortcut Input)
2. Cherche une URL dans cette entrée avec "Faire correspondre le texte" (pattern: https?://[^\\s]+)
3. Si une URL est trouvée, utilise-la comme LIEN
4. Si aucune URL n'est trouvée, récupère le contenu du presse-papiers et cherche une URL dedans
5. Si toujours pas d'URL, utilise l'entrée complète comme TEXTE (ce sera le titre)

Ensuite fais une requête GET vers :
${PROD_URL}/api/targets/quick-add?token=${token}&url=[LIEN ou vide]&text=[TEXTE complet de l'entrée]

Si la réponse JSON contient "success": true, affiche la notification "Cible ajoutée ✅"
Sinon affiche l'alerte avec le message d'erreur.

Important : envoie TOUJOURS le paramètre "text" avec le contenu complet partagé, même si tu as trouvé une URL.`;
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedPrompt(true);
      toast.success('Prompt copié ! Colle-le dans l\'app Raccourcis.');
      setTimeout(() => setCopiedPrompt(false), 3000);
    } catch (e) {
      toast.error('Erreur de copie');
    }
  };

  return (
    <Card className="bg-slate-900/50 border-fuchsia-500/30">
      <CardHeader>
        <CardTitle className="text-lg font-orbitron uppercase tracking-wider text-fuchsia-400 flex items-center gap-2">
          <Smartphone className="w-5 h-5" />
          Raccourci iOS
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-400">
          Partage un lien depuis n'importe quelle app → la cible est ajoutée automatiquement.
        </p>

        {loading ? (
          <div className="text-slate-500 text-sm">Chargement...</div>
        ) : token ? (
          <div className="space-y-4">
            
            {/* Instructions simplifiées */}
            <div className="bg-fuchsia-950/30 rounded-lg p-4 border border-fuchsia-500/20 space-y-4">
              <h4 className="text-sm font-semibold text-fuchsia-300">📱 Configuration (iOS 18+)</h4>
              
              <ol className="text-xs text-slate-400 space-y-3 list-decimal list-inside">
                <li>Ouvre l'app <strong className="text-slate-200">Raccourcis</strong> sur ton iPhone</li>
                <li>Appuie sur <strong className="text-slate-200">+</strong> pour créer un nouveau raccourci</li>
                <li>Copie et colle ce prompt :</li>
              </ol>

              {/* Prompt à copier */}
              <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                <p className="text-[11px] text-slate-300 mb-3 leading-relaxed">
                  Le prompt amélioré extrait l'URL du lien partagé <strong className="text-fuchsia-400">ou du presse-papiers</strong>. 
                  Si aucune URL n'est trouvée, le titre est envoyé et Auto Edito recherche automatiquement l'article.
                </p>
                <Button
                  size="sm"
                  className="w-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white h-8 text-xs"
                  onClick={copyPrompt}
                >
                  {copiedPrompt ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                  {copiedPrompt ? 'Prompt copié !' : 'Copier le prompt amélioré'}
                </Button>
              </div>

              <ol className="text-xs text-slate-400 space-y-2 list-decimal list-inside" start={4}>
                <li>Nomme le raccourci <strong className="text-slate-200">"Auto Edito"</strong></li>
                <li>C'est prêt ! Partage un lien pour tester 🎉</li>
              </ol>
            </div>

            {/* Token (pour référence) */}
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-slate-500 uppercase">Ton token secret</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-5 text-[10px] text-slate-500 hover:text-fuchsia-400 px-2"
                  onClick={copyToken}
                >
                  {copiedToken ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                </Button>
              </div>
              <code className="text-[10px] text-slate-500 break-all font-mono">{token}</code>
            </div>

            {/* Bouton régénérer */}
            <Button
              variant="outline"
              size="sm"
              className="w-full border-slate-600 text-slate-400 hover:text-fuchsia-400 hover:border-fuchsia-500"
              onClick={generateToken}
              disabled={generating}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
              Régénérer le token (invalide l'ancien)
            </Button>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-slate-500 mb-4">
              Génère un token pour activer le raccourci iOS
            </p>
            <Button
              className="bg-fuchsia-600 hover:bg-fuchsia-500"
              onClick={generateToken}
              disabled={generating}
            >
              <Smartphone className="w-4 h-4 mr-2" />
              Générer mon token
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default IOSShortcutCard;
